import { useState } from 'react';
import type { InventoryFacet } from '../../lib/api/operatorConsole';
import { Checkbox } from './Checkbox';

const COLLAPSED_LIMIT = 6;

interface Props {
  title: string;
  options: InventoryFacet[];
  /** Selected option ids, always strings (they travel in the URL). */
  selected: string[];
  onChange: (selected: string[]) => void;
}

/** One facet of the inventory filter: checkbox per option with its count,
 *  long lists collapsed behind "Show all". Renders nothing when empty. */
export function FacetGroup({ title, options, selected, onChange }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (options.length === 0) return null;

  const shown = expanded ? options : options.slice(0, COLLAPSED_LIMIT);

  const toggle = (id: string, checked: boolean) => {
    onChange(checked ? [...selected, id] : selected.filter((s) => s !== id));
  };

  return (
    <fieldset className="border-0 p-0">
      <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
        {title}
      </legend>
      <div className="flex flex-col gap-1">
        {shown.map((option) => {
          const id = String(option.id);
          return (
            // Set names run long ("2025 POKEMON JAPANESE M-P PROMO…") and the
            // filter column is narrow, so the caption wraps over as many lines
            // as it needs and the count keeps its own place beside it.
            <div key={id} className="flex items-start justify-between gap-2">
              <Checkbox
                aria-label={`${title}: ${option.label}`}
                label={option.label}
                wrap
                checked={selected.includes(id)}
                onChange={(checked) => toggle(id, checked)}
              />
              <span className="shrink-0 font-mono text-[11px] leading-5 text-[var(--co-muted)]">
                {option.count}
              </span>
            </div>
          );
        })}
      </div>
      {options.length > COLLAPSED_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] text-[var(--co-muted)] underline-offset-2 hover:underline"
        >
          {expanded ? 'Show less' : `Show all (${options.length})`}
        </button>
      )}
    </fieldset>
  );
}
